import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Typography, TextField, Button, Paper, CircularProgress, Alert
} from '@mui/material';
import { Payments, ContentCopy, Check } from '@mui/icons-material';
import { API_BASE_URL, FACTURACION_GET, PAGO_LICENCIA_SUBIR } from '../services/apiConfig';
import { avisar } from '../services/toast';

const fmtMoneda = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ALIAS_PAGO = 'martin.squeff';

// Aviso de vencimiento para el cartel de pago. dias_restantes = null si el sitio
// no tiene fecha de vencimiento cargada (en ese caso no se dice nada de días).
const textoVencimiento = (licencia) => {
  if (!licencia) return null;
  if (licencia.expirado) return 'Tu licencia venció. No dejes de pagar para seguir usando el sistema.';
  const dias = licencia.dias_restantes;
  if (dias === null || dias === undefined) return null;
  if (dias <= 0) return 'Falta menos de 1 día para que tu licencia expire. ¡No dejes de pagar!';
  const faltan = dias === 1 ? 'Falta 1 día' : `Faltan ${dias} días`;
  return `${faltan} para que tu licencia expire. ¡No dejes de pagar!`;
};

function PagoLicenciaForm() {
  const [precioPorSucursal, setPrecioPorSucursal] = useState(0);
  const [cantidadSucursales, setCantidadSucursales] = useState(0);
  const [montoTotal, setMontoTotal] = useState(0);
  const [fuentePrecio, setFuentePrecio] = useState('manual');
  const [productoReferenciaDescripcion, setProductoReferenciaDescripcion] = useState(null);
  const [precioBase, setPrecioBase] = useState(null);
  const [multiplicador, setMultiplicador] = useState(2);
  const [fechaPago, setFechaPago] = useState(new Date().toISOString().split('T')[0]);
  const [archivo, setArchivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [licencia, setLicencia] = useState(null);
  const [aliasCopiado, setAliasCopiado] = useState(false);

  useEffect(() => {
    const fetchMonto = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(API_BASE_URL + FACTURACION_GET, { headers });
        if (response.data.success && response.data.data) {
          const d = response.data.data;
          // Precio por sucursal, cantidad y total ya vienen calculados desde
          // el backend (config/licencia_helper.php) — puede salir de un
          // producto de referencia (indexado) o del monto manual, ver fuente.
          setPrecioPorSucursal(parseFloat(d.monto_por_sucursal) || 0);
          setCantidadSucursales(parseInt(d.cantidad_sucursales, 10) || 0);
          setMontoTotal(parseFloat(d.monto_total) || 0);
          setFuentePrecio(d.fuente_precio_licencia || 'manual');
          setProductoReferenciaDescripcion(d.producto_referencia_descripcion || null);
          setPrecioBase(d.producto_referencia_precio_base != null ? parseFloat(d.producto_referencia_precio_base) : null);
          setMultiplicador(parseFloat(d.producto_referencia_multiplicador_efectivo) || 2);
          setLicencia(d.licencia || null);
        }
      } catch (error) {
        console.error('Error al obtener monto mensual:', error);
      }
    };
    fetchMonto();
  }, []);

  const copiarAlias = async () => {
    try {
      await navigator.clipboard.writeText(ALIAS_PAGO);
      setAliasCopiado(true);
      setTimeout(() => setAliasCopiado(false), 2000);
    } catch (error) {
      avisar(`No se pudo copiar. El alias es ${ALIAS_PAGO}`, 'warning');
    }
  };

  const avisoVencimiento = textoVencimiento(licencia);
  const severidadAviso = licencia?.expirado ? 'error'
    : (licencia?.dias_restantes != null && licencia.dias_restantes <= 7 ? 'warning' : 'info');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!archivo) {
      avisar('Debe adjuntar un comprobante', 'warning');
      return;
    }
    if (!fechaPago) {
      avisar('Debe seleccionar una fecha', 'warning');
      return;
    }

    setEnviando(true);
    try {
      const formData = new FormData();
      formData.append('comprobante', archivo);
      formData.append('fecha_pago', fechaPago);

      const token = sessionStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } : { 'Content-Type': 'multipart/form-data' };

      const response = await axios.post(API_BASE_URL + PAGO_LICENCIA_SUBIR, formData, { headers });

      if (response.data.status === 'success') {
        avisar('Comprobante enviado correctamente', 'success');
        setArchivo(null);
        document.getElementById('file-input').value = '';
      } else {
        avisar(response.data.message || 'Error al enviar', 'error');
      }
    } catch (error) {
      avisar('Error de conexión: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Payments color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h6">Informar Pago de Licencia</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Adjunte el comprobante de pago mensual. Se enviará automáticamente por email.
      </Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.5, mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Precio por sucursal</Typography>
          <Typography variant="body2">{fmtMoneda(precioPorSucursal)}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 0.5, mb: 1 }}>
          <Typography variant="body2" color="text.secondary">Sucursales activas</Typography>
          <Typography variant="body2">× {cantidadSucursales}</Typography>
        </Box>
        {fuentePrecio === 'producto' && productoReferenciaDescripcion && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 0.5, mb: 1 }}>
            Precio por sucursal según el valor actual de "{productoReferenciaDescripcion}"
            {precioBase != null && ` (${fmtMoneda(precioBase)} × ${multiplicador})`}.
          </Typography>
        )}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          px: 1, py: 1, mb: 1.5, borderRadius: 1, bgcolor: 'action.hover',
        }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Total a pagar</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{fmtMoneda(montoTotal)}</Typography>
        </Box>
        <Alert severity={severidadAviso} sx={{ mb: 1.5 }}>
          {avisoVencimiento && (
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>{avisoVencimiento}</Typography>
          )}
          <Typography variant="body2" component="div">
            Podés pagar por transferencia a este alias: <strong>{ALIAS_PAGO}</strong> (cuenta Santander, a nombre de Martin Squeff).
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={copiarAlias}
            startIcon={aliasCopiado ? <Check /> : <ContentCopy />}
            sx={{ mt: 1 }}
          >
            {aliasCopiado ? 'Alias copiado' : 'Copiar alias'}
          </Button>
        </Alert>
        <TextField
          label="Fecha de pago"
          type="date"
          fullWidth
          size="small"
          value={fechaPago}
          onChange={(e) => setFechaPago(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 1.5 }}
        />
        <Button
          variant="outlined"
          component="label"
          fullWidth
          sx={{ mb: 1.5, textAlign: 'left', justifyContent: 'flex-start' }}
        >
          {archivo ? archivo.name : 'Seleccionar comprobante'}
          <input
            id="file-input"
            type="file"
            hidden
            accept="image/*,application/pdf"
            onChange={(e) => setArchivo(e.target.files[0])}
          />
        </Button>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={enviando}
          startIcon={enviando ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {enviando ? 'Enviando...' : 'Enviar comprobante'}
        </Button>
      </Box>
    </Paper>
  );
}

export default PagoLicenciaForm;
